import { useState, useEffect, useRef } from 'react';
import { useAuth } from 'context/AuthContext';
import usePageTitle from 'hooks/usePageTitle';
import api, { urlDocument } from 'services/api';
import ImageProtegee from 'components/ui/ImageProtegee';
import { limitePour, preparerFichier, formaterPoids } from 'services/televersement';

/* ═══════════════════════════════════════════════════════════════════════════
 * MON PROFIL — L'ÉCRAN N'AFFICHE JAMAIS UN RÉSULTAT NON CONFIRMÉ
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ LE DÉFAUT CORRIGÉ (04/09/2026) : l'aperçu venait d'un
 *    `URL.createObjectURL(fichier)` local et RIEN ne le distinguait d'une photo
 *    réellement enregistrée. L'utilisateur voyait sa nouvelle photo à la
 *    seconde où il la choisissait, avant tout appel au serveur — puis n'en
 *    avait plus aucune trace ailleurs dans le produit.
 *
 *    ⓘ La chaîne SERVEUR, elle, fonctionnait : mesuré le 04/09 en local —
 *      `PUT /api/users/profile` → 200, `utilisateurs.avatar_url` renseignée,
 *      fichier sur disque, relecture par `/api/documents/photo-utilisateur`
 *      → 200 avec jeton, **401 sans jeton**. Le défaut était entièrement ici.
 *
 * ⚠️ RÈGLE : « Une interface n'affiche jamais le résultat d'une opération
 *    avant que le serveur l'ait confirmée. Un aperçu local est marqué comme
 *    non enregistré, ou n'est pas montré. »
 *    ➡️ Ici il est MARQUÉ : bandeau « non enregistrée », bordure d'attente.
 *
 * ⚠️ AUCUNE LIMITE DE TAILLE N'EST ÉCRITE DANS CE FICHIER. Elle vient de
 *    `GET /api/limites-televersement` — la table que multer applique.
 *    Avant, « max 2MB » était écrit ici, en dur, et c'était FAUX : Nginx
 *    coupait à 1 Mo.
 */

export default function Profil() {
  const { user, updateUser } = useAuth();
  usePageTitle('Mon profil');

  const [form, setForm] = useState({
    nom: user?.nom || '',
    prenom: user?.prenom || '',
    telephone: user?.telephone || '',
  });
  const [photo, setPhoto] = useState(null);          // fichier prêt à partir
  const [apercuLocal, setApercuLocal] = useState(null); // NON ENREGISTRÉ
  const [photoServeur, setPhotoServeur] = useState(
    user?.avatar_url ? urlDocument('photo-utilisateur', user.id) : null
  );
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: '', error: false });
  const [regle, setRegle] = useState(null);
  const champFichier = useRef(null);

  // La règle de l'usage « photo_profil » : poids, formats, redimensionnement.
  useEffect(() => {
    let vivant = true;
    limitePour('photo_profil').then((r) => { if (vivant) setRegle(r); });
    return () => { vivant = false; };
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const choisirFichier = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setMsg({ text: '', error: false });

    // ⚠️ Le fichier est REDIMENSIONNÉ ICI, avant tout envoi : côté le plus long
    //    à 512 px, JPEG 0,85 — une photo de téléphone de 4 Mo part à ~150 Ko.
    //    Sur une connexion 3G, c'est la différence entre instantané et échec.
    const { fichier, erreur } = await preparerFichier(f, 'photo_profil');
    if (erreur) {
      setMsg({ text: erreur, error: true });
      setPhoto(null);
      setApercuLocal(null);
      if (champFichier.current) champFichier.current.value = '';
      return;
    }
    setPhoto(fichier);
    setApercuLocal(URL.createObjectURL(fichier));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;                    // aucun double envoi possible
    setSaving(true);
    setMsg({ text: '', error: false });
    try {
      const formData = new FormData();
      formData.append('nom', form.nom);
      formData.append('prenom', form.prenom);
      formData.append('telephone', form.telephone);
      if (photo) formData.append('photo', photo);
      const res = await api.put('/users/profile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // ⚠️ L'APERÇU LOCAL EST RETIRÉ ET REMPLACÉ PAR L'IMAGE RELUE DU SERVEUR.
      //    `?v=` casse le cache navigateur : sans lui, l'ancienne photo
      //    resterait affichée et l'enregistrement paraîtrait ne rien faire.
      if (res.data?.avatar_url) {
        setPhotoServeur(`${urlDocument('photo-utilisateur', user.id)}?v=${Date.now()}`);
      }
      if (apercuLocal) URL.revokeObjectURL(apercuLocal);
      setApercuLocal(null);
      setPhoto(null);
      if (champFichier.current) champFichier.current.value = '';  // le nom du fichier disparaît

      // updateUser MERGE (il ne remplace pas) : les rôles du contexte survivent.
      updateUser(res.data);
      setMsg({ text: 'Profil enregistré.', error: false });
    } catch (err) {
      // ⚠️ JAMAIS UN CODE HTTP BRUT À L'ÉCRAN. Le serveur renvoie déjà un
      //    message français qui nomme la cause et la limite ; on le montre.
      let detail = err.response?.data?.error;
      if (!detail && err.response?.status === 413) {
        detail = regle
          ? `Fichier trop lourd. Maximum : ${formaterPoids(regle.octets)}.`
          : 'Fichier trop lourd.';
      }
      if (!detail) detail = "L'enregistrement a échoué. Vérifiez votre connexion et réessayez.";
      setMsg({ text: detail, error: true });

      // ⚠️ À L'ÉCHEC, L'APERÇU LOCAL EST RETIRÉ ET L'ANCIENNE PHOTO REVIENT.
      //    Laisser la nouvelle image à l'écran après un échec, c'est mentir.
      if (apercuLocal) URL.revokeObjectURL(apercuLocal);
      setApercuLocal(null);
      setPhoto(null);
      if (champFichier.current) champFichier.current.value = '';
    }
    setSaving(false);
  };

  const imageAffichee = apercuLocal || photoServeur;

  return (
    <div>
      <div className="content-card" style={{ maxWidth: 600 }}>
        {msg.text && (
          <div className={`gss-alert ${msg.error ? 'gss-alert-error' : ''}`}
            style={!msg.error
              ? { background: 'rgba(26,135,84,0.06)', color: '#1A8754', border: '1px solid rgba(26,135,84,0.15)', marginBottom: '1rem' }
              : { marginBottom: '1rem' }}>
            {msg.text}
          </div>
        )}

        {imageAffichee && (
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <ImageProtegee
              src={imageAffichee}
              alt="Photo de profil"
              style={{
                width: 80, height: 80, borderRadius: '50%', objectFit: 'cover',
                // L'aperçu non enregistré SE VOIT : bordure d'attente, pas grise.
                border: apercuLocal ? '2px dashed var(--gss-red)' : '2px solid #ddd',
                opacity: apercuLocal ? 0.8 : 1,
              }}
            />
            {apercuLocal && (
              <div style={{ fontSize: 11, color: 'var(--gss-red)', marginTop: 6, fontWeight: 600 }}>
                Photo non enregistrée — cliquez sur « Enregistrer »
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="gss-form-grid">
            <div className="gss-field">
              <label>Prenom</label>
              <input className="gss-input" name="prenom" value={form.prenom} onChange={handleChange} />
            </div>
            <div className="gss-field">
              <label>Nom</label>
              <input className="gss-input" name="nom" value={form.nom} onChange={handleChange} />
            </div>
            <div className="gss-field">
              <label>Email</label>
              {/* ⓘ AUCUN PLACEHOLDER ICI, ET IL N'Y EN A JAMAIS EU (vérifié le
                  04/09 : grep sur tout le frontend). Ce champ affiche la VRAIE
                  adresse du compte. Si elle porte le domaine de l'éditeur, c'est
                  que le compte a été créé ainsi — c'est une donnée à corriger,
                  pas un défaut de code : changer cette adresse change
                  l'identifiant de connexion, donc cela ne se fait pas ici. */}
              <input className="gss-input" type="email" value={user?.email || ''} disabled
                style={{ cursor: 'not-allowed', background: 'var(--gss-bg)', color: 'var(--gss-text)' }} />
              <div style={{ fontSize: 11, color: 'var(--gss-text-light)', marginTop: 4 }}>
                Adresse de connexion. Sa modification passe par un administrateur.
              </div>
            </div>
            <div className="gss-field">
              <label>Telephone</label>
              <input className="gss-input" name="telephone" value={form.telephone} onChange={handleChange} />
            </div>
            <div className="gss-field gss-field-full">
              <label>
                Photo de profil
                {/* ⚠️ Formats et poids LUS DANS LA TABLE, jamais écrits ici.
                    Tant qu'elle n'est pas chargée, on n'annonce aucun chiffre —
                    une limite devinée qui se révèle fausse, c'est le 413. */}
                {regle && ` (${regle.libelle_formats}, maximum ${formaterPoids(regle.octets)})`}
              </label>
              <input
                ref={champFichier}
                type="file"
                accept={regle ? regle.extensions.join(',') : 'image/jpeg,image/png'}
                className="gss-input"
                style={{ paddingTop: '10px' }}
                onChange={choisirFichier}
              />
              {regle?.redimensionner && (
                <div style={{ fontSize: 11, color: 'var(--gss-text-light)', marginTop: 4 }}>
                  Votre photo est réduite à {regle.redimensionner.cote_max} px avant l’envoi :
                  elle part en quelques kilo-octets, même en connexion lente.
                </div>
              )}
            </div>
          </div>
          <div style={{ marginTop: '1.5rem' }}>
            <button className="gss-btn gss-btn-primary" type="submit" disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
